import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { TooltipHost } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";

import InstancesListing from "../instances-listing/InstancesListing";
import { bactInstanceApiCall, instanceApiCall } from "../redux/apiCalls";
import { startFetchingAssociationInstancesAttachFromIci } from "../redux/slice";
import { setSelectedAssociationData, updateAssociationInstanceData, updateSetDocsForAttachToEmailData } from "../utils";
import { TEXT } from "../../../shared/constants/texts";
import { PAGE, PAGE_SIZE } from "../../../shared/constants/paginationConstants";
import { ICON } from "../../../shared/constants/icons";
import { logError } from "../../../shared/services/errorLogging";

const AssociationListing: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const {
    isFetchingAssociationData,
    associationData,
    selectedAssociation,
    selectedAgreement,
    isFetchingAssociationInstances,
    associationInstancesData,
    docsToAttach,
  } = state;
  const [showInstancesId, setShowInstancesId] = React.useState(null);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (showInstancesId) {
      getAssociationInstances();
    } else {
      updateAssociationInstanceData([]);
    }
  }, [showInstancesId]);

  const getAssociationInstances = async () => {
    dispatch(startFetchingAssociationInstancesAttachFromIci());
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateAssociationInstanceData(response);
      }
      if (error) {
        updateAssociationInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|attach-from-ici|association-listing|AssociationListing - getAssociationInstances",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateAssociationInstanceData(response);
      }
      if (error) {
        updateAssociationInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|attach-from-ici|association-listing|AssociationListing - getAssociationInstances",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const onIconToggleClicked = (data) => {
    if (showInstancesId === data.EntityInstanceId) {
      setShowInstancesId(null);
      setSelectedAssociationData(null);
    } else {
      setShowInstancesId(data.EntityInstanceId);
      setSelectedAssociationData(data);
    }
    const docs = [...docsToAttach];
    const updatedDoc = docs.filter((doc) => doc.sysId);
    updateSetDocsForAttachToEmailData(updatedDoc);
  };

  const onEnterPressAssociation = (e, association) => {
    if (e.key === TEXT.ENTER || e.keyCode === TEXT.ENTER_KEY_CODE) {
      onIconToggleClicked(association);
    }
  };

  return isFetchingAssociationData ? (
    <LoadingAssociations />
  ) : (
    <div className="association-listing-container-attach-from-ici">
      {associationData && associationData.length > 0 ? (
        associationData.map((association) => {
          let classNameForAssociation = "single-association-container-attach-from-ici";
          if (selectedAssociation && selectedAssociation.EntityInstanceId === association.EntityInstanceId) {
            classNameForAssociation = "single-association-container-selected-attach-from-ici";
          }
          const iconName = showInstancesId === association.EntityInstanceId ? ICON.UP : ICON.DOWN;
          return (
            <React.Fragment key={association.EntityInstanceId}>
              <div className={classNameForAssociation}>
                <div
                  className="icon-toggle"
                  onClick={() => onIconToggleClicked(association)}
                  tabIndex={1}
                  onKeyDown={(e) => onEnterPressAssociation(e, association)}
                  title={iconName}
                >
                  <Icon iconName={iconName} />
                  <span className="sr-only">{iconName}</span>
                </div>
                <div className="association-label">
                  <TooltipHost id="association-tooltip-attach-from-ici" content={association.name}>
                    <p>{association.name} </p>
                  </TooltipHost>
                </div>
              </div>
              {showInstancesId && showInstancesId === association.EntityInstanceId ? (
                isFetchingAssociationInstances ? (
                  <LoadingAssociationIntances />
                ) : associationInstancesData && associationInstancesData.length > 0 ? (
                  <div
                    className="association-instance-listing-container-attach-from-ici"
                    id="id-association-instances-listing-attach-from-ici"
                  >
                    <InstancesListing
                      data={associationInstancesData}
                      idParam="id-association-instances-listing-attach-from-ici"
                      loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
                      endMsg=""
                      hasMoreData={false}
                      loadMoreData={() => {}}
                    />
                  </div>
                ) : (
                  <EmptyAssociationInstances />
                )
              ) : null}
            </React.Fragment>
          );
        })
      ) : (
        <EmptyAssociations />
      )}
    </div>
  );
};

const LoadingAssociations = () => {
  return (
    <div className="association-listing-loading-container-attach-from-ici">
      <Spinner label={TEXT.LOADING_ASSOCIATIONS} labelPosition="right" />
    </div>
  );
};

const EmptyAssociations = () => {
  return (
    <div className="association-listing-empty-container-attach-from-ici">
      <p>{TEXT.NO_ASSOCIATION_FOUND}</p>
    </div>
  );
};

const LoadingAssociationIntances = () => {
  return (
    <div className="association-instances-listing-loading-container-attach-from-ici">
      <Spinner label={TEXT.LOADING_ASSOCIATION_INSTANCES} labelPosition="right" />
    </div>
  );
};

const EmptyAssociationInstances = () => {
  return (
    <div className="association-instances-listing-empty-container-attach-from-ici">
      <p>{TEXT.NO_ASSOCIATION_INSTANCES_FOUND}</p>
    </div>
  );
};

export default AssociationListing;
